'use client';

import { useEffect } from 'react';

import logger from '@/lib/logger';

export function useGlobalErrorLogger() {
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      logger.error('[Global Error]', {
        message: event.message,
        source: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error ? serializeError(event.error) : undefined,
      });
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const { reason, promise } = event;

      const errorDetails = serializeUnknownError(reason);
      const promiseDetails = serializeUnknownError(promise);

      logger.error('[Unhandled Promise Rejection]', {
        errorDetails,
        promise: promiseDetails,
      });
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener(
        'unhandledrejection',
        handleUnhandledRejection
      );
    };
  }, []);
}

function serializeError(error: Error): Record<string, unknown> {
  const base = {
    type: error.name,
    message: error.message,
    stack: error.stack,
  };

  const customProps = Object.getOwnPropertyNames(error).reduce(
    (acc, key) => {
      acc[key] = (error as any)[key];
      return acc;
    },
    {} as Record<string, unknown>
  );

  return { ...base, ...customProps };
}

function serializeUnknownError(reason: unknown): Record<string, unknown> {
  if (reason instanceof Error) {
    return serializeError(reason);
  }

  if (typeof reason === 'object' && reason !== null) {
    return {
      type: reason.constructor?.name || 'UnknownObjectError',
      code: (reason as any).code || 'UnknownErrorCode',
      message: (reason as any).message || 'Unknown error occurred',
      stack: (reason as any).stack || 'No stack trace available',
      ...reason,
    };
  }

  return {
    type: typeof reason,
    value: reason,
  };
}
